// Tiny external store for leg-level selection in the preplan unassigned grid.
// Designed for useSyncExternalStore — primitive/ref-stable snapshots so only
// affected cells re-render. Backward-compat: empty when isLegWise=false.

let state = { selectedLegs: {} };
const listeners = new Set();

const emit = () => {
  listeners.forEach((l) => l());
};

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// --- snapshot getters ---
// Primitive (string | null) — ref-stable across unrelated mutations.
const getLegSnapshot = (orderId, type) => state.selectedLegs[orderId]?.[type] ?? null;

// Per-order legs object — ref preserved when this order untouched.
const getRowSnapshot = (orderId) => state.selectedLegs[orderId] ?? null;

// Whole map — changes ref on every mutation. Use sparingly (header cells).
const getStateSnapshot = () => state.selectedLegs;

// --- mutations ---
const toggleLeg = (orderId, type, locationId) => {
  const cur = state.selectedLegs[orderId] || {};
  const next = { ...state.selectedLegs };
  if (cur[type]) {
    const row = { ...cur };
    delete row[type];
    if (Object.keys(row).length === 0) delete next[orderId];
    else next[orderId] = row;
  } else {
    next[orderId] = { ...cur, [type]: locationId };
  }
  state = { selectedLegs: next };
  emit();
};

// availableLegs: { PICKUP?: locationId, DELIVERY?: locationId } — only legs that exist on the order.
const toggleRow = (orderId, availableLegs) => {
  const cur = state.selectedLegs[orderId];
  const availKeys = Object.keys(availableLegs);
  const curKeys = cur ? Object.keys(cur) : [];
  const allSelected =
    availKeys.length > 0 &&
    availKeys.length === curKeys.length &&
    availKeys.every((k) => cur[k] === availableLegs[k]);
  const next = { ...state.selectedLegs };
  if (allSelected) {
    delete next[orderId];
  } else {
    next[orderId] = { ...availableLegs };
  }
  state = { selectedLegs: next };
  emit();
};

// rowsWithLeg: [{ orderId, locationId }]
const toggleAllByLeg = (type, rowsWithLeg) => {
  if (!rowsWithLeg || rowsWithLeg.length === 0) return;
  const allSelected = rowsWithLeg.every(
    (r) => state.selectedLegs[r.orderId]?.[type] === r.locationId
  );
  const next = { ...state.selectedLegs };
  rowsWithLeg.forEach((r) => {
    const cur = next[r.orderId] || {};
    if (allSelected) {
      const row = { ...cur };
      delete row[type];
      if (Object.keys(row).length === 0) delete next[r.orderId];
      else next[r.orderId] = row;
    } else {
      next[r.orderId] = { ...cur, [type]: r.locationId };
    }
  });
  state = { selectedLegs: next };
  emit();
};

const replaceAll = (selectedLegs) => {
  state = { selectedLegs: selectedLegs || {} };
  emit();
};

const clear = () => replaceAll({});

export const legSelectionStore = {
  subscribe,
  getLegSnapshot,
  getRowSnapshot,
  getStateSnapshot,
  toggleLeg,
  toggleRow,
  toggleAllByLeg,
  replaceAll,
  clear,
};

// --- pure helpers ---
export const getAvailableLegs = (order) => {
  const legs = {};
  (order?.locations || []).forEach((l) => {
    if (l.type_of_loc === "PICKUP") legs.PICKUP = l.id;
    if (l.type_of_loc === "DELIVERY") legs.DELIVERY = l.id;
  });
  return legs;
};

export const getRowCheckState = (availableLegs, selectedLegsForOrder) => {
  const availCount = Object.keys(availableLegs).length;
  if (availCount === 0) return "disabled";
  const selCount = selectedLegsForOrder ? Object.keys(selectedLegsForOrder).length : 0;
  if (selCount === 0) return "unchecked";
  if (selCount === availCount) return "checked";
  return "indeterminate";
};
